import { useState, useEffect, useCallback } from 'react';
import {
  listActivitiesByChapter,
  createActivity,
  updateActivity,
  updateActivityContent,
  deleteActivity
} from '@dataconnect/generated';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCog } from 'react-icons/fa';
import { getActivityComponent, validateActivityContent, getDefaultContent } from './activity';

// Content Popup Component
const ContentPopup = ({ show, activityType, contentType, onSave, onClose, initialContent, selectedChapter, activityId }) => {
  const [content, setContent] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  const [saving, setSaving] = useState(false);

  // Initialize content when popup opens or activity type changes
  useEffect(() => {
    if (show && activityType) {
      if (initialContent) {
        try {
          setContent(JSON.parse(initialContent));
        } catch {
          setContent(getDefaultContent(activityType));
        }
      } else {
        setContent(getDefaultContent(activityType));
      }
    }
  }, [show, activityType, initialContent]);

  // Validate content whenever it changes
  useEffect(() => {
    if (activityType && Object.keys(content).length > 0) {
      const errors = validateActivityContent(activityType, content);
      setValidationErrors(errors);
    }
  }, [content, activityType]);

  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
  }, []);

  const handleSave = async () => {
    // Final validation before saving
    const errors = validateActivityContent(activityType, content);
    if (errors.length === 0) {
      if (activityId) {
        // Direct save to DB if editing existing activity
        try {
          setSaving(true);
          await updateActivityContent({
            id: activityId,
            baseContent: JSON.stringify(content)
          });
          onSave(content); // Still call this to update parent state and close popup
        } catch (err) {
          alert('Failed to save to database: ' + err.message);
        } finally {
          setSaving(false);
        }
      } else {
        // Just update parent state for new activity
        onSave(content);
      }
    } else {
      alert('Please fix validation errors before saving:\n' + errors.join('\n'));
    }
  };

  if (!show) return null;

  const ActivityComponent = getActivityComponent(activityType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Edit {contentType === 'base' ? 'Base' : 'Practice'} Content
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Activity Type: <span className="font-medium text-blue-600">{activityType}</span>
            </p>
          </div>
          <button 
            type="button" 
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            onClick={onClose}
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {ActivityComponent ? (
            <ActivityComponent
              activityId={activityId}
              initialContent={initialContent}
              onContentChange={handleContentChange}
              chapterId={selectedChapter?.id}
              grade={selectedChapter?.grade}
              selectedChapter={selectedChapter}
            />
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                  <FaCog className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-800">Activity Type Not Supported</h3>
                  <p className="text-amber-700">The activity type &quot;{activityType}&quot; does not have a dedicated editor yet.</p>
                </div>
              </div>
              <p className="text-amber-700 mb-4">Please use the JSON editor below:</p>
              <textarea
                className="w-full h-64 p-4 border border-amber-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                value={JSON.stringify(content, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setContent(parsed);
                  } catch {
                    // Invalid JSON, don't update
                  }
                }}
                placeholder="Enter valid JSON content..."
              />
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="max-w-xl">
            {validationErrors.length > 0 && (
              <div>
                <div className="flex items-center text-red-600 mb-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">
                    {validationErrors.length} validation error(s)
                  </span>
                </div>
                <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex space-x-3">
            <button 
              type="button" 
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                validationErrors.length > 0 || saving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              onClick={handleSave}
              disabled={validationErrors.length > 0 || saving}
            >
              <FaSave className={`w-4 h-4 mr-2 inline ${saving ? 'animate-spin' : ''}`} />
              {saving ? 'Saving to DB...' : 'Save Content'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminActivityEditor = ({ selectedChapter, onBack }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    activityType: '',
    baseContent: '',
    order: '',
    autoGenerateImages: false
  });

  // Popup states
  const [showContentPopup, setShowContentPopup] = useState(false);
  const [contentType, setContentType] = useState(''); // 'base' or 'practice'

  // Activity type options
  // Note: CREATIVE_WRITING is not included here as it's auto-generated based on chapter type
  const activityTypes = [
    { value: 'MATCH', label: 'Match the Following' },
    { value: 'WORD_MEANING', label: 'Word Meanings' },
    { value: 'DUAL_MEANING', label: 'Dual Meaning' },
    { value: 'OPPOSITES', label: 'Opposites' },
    { value: 'MAKE_SENTENCES', label: 'Make Sentences' },
    { value: 'WHO_SAID_TO_WHOM', label: 'Who Said To Whom' },
    { value: 'QUESTION_ANSWERS', label: 'Question Answers' },
    { value: 'LONG_QUESTION_ANSWERS', label: 'Long Question Answers' },
    { value: 'WORD_PRACTICE', label: 'Word Practice' },
    { value: 'WORD_SPLIT', label: 'Word Split' },
    { value: 'FILL_IN_BLANK_MCQ', label: 'Fill in the Blanks (MCQ)' },
    { value: 'NUMBERS', label: 'Numbers' },
    { value: 'GENDERS', label: 'Genders' },
    { value: 'CHANGE_GENDERS', label: 'Change Genders' },
    { value: 'TENSES', label: 'Tenses' },
    { value: 'SINGULAR_PLURAL', label: 'Singular Plural' },
    { value: 'JOIN_WORDS', label: 'Join Words' },
    { value: 'CUSTOM', label: 'Custom Activity' }
  ];


  // Load activities for the chapter
  useEffect(() => {
    if (selectedChapter?.id) {
      loadActivities();
    }
  }, [selectedChapter]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const { data } = await listActivitiesByChapter({
        chapterId: selectedChapter.id
      }, { fetchPolicy: 'no-cache' });
      setActivities(data.activities || []);
    } catch (err) {

      setError('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  // Validation: Check if activity type already exists for this chapter
  const validateActivityType = (activityType, excludeId = null) => {
    return activities.some(activity =>
      activity.activityType === activityType && activity.id !== excludeId
    );
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      activityType: '',
      baseContent: '',
      order: '',
      autoGenerateImages: false
    });
    setShowAddForm(false);
    setEditingId(null);
    setShowContentPopup(false);
    setContentType('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.activityType || !formData.baseContent) {
      alert('Activity Type and Base Content are required');
      return;
    }

    // Check for duplicate activity type (should not happen due to UI prevention, but keeping as safety check)
    if (validateActivityType(formData.activityType, editingId)) {
      alert(`An activity of type "${activityTypes.find(t => t.value === formData.activityType)?.label}" already exists for this chapter. Please choose a different activity type.`);
      return;
    }

    // Validate content using activity-specific validators
    const baseContentErrors = validateActivityContent(formData.activityType, formData.baseContent);
    if (baseContentErrors.length > 0) {
      alert('Base Content validation failed:\n' + baseContentErrors.join('\n'));
      return;
    }

    // Parse optional order as integer
    const orderTrimmed = String(formData.order ?? '').trim();
    let orderValue = null;
    if (orderTrimmed !== '') {
      const o = Number(orderTrimmed);
      if (!Number.isInteger(o)) {
        alert('Order must be an integer.');
        return;
      }
      orderValue = o;
    }

    try {
      setSaving(true);

      let savedActivityId = editingId;
      if (editingId) {
        // Update existing activity
        await updateActivity({
          id: editingId,
          activityType: formData.activityType,
          chapterId: selectedChapter.id,
          baseContent: formData.baseContent,
          scopeLevel: 'GLOBAL',
          scopeId: null,
          order: orderValue
        });
      } else {
        // Create new activity
        const createResult = await createActivity({
          activityType: formData.activityType,
          chapterId: selectedChapter.id,
          baseContent: formData.baseContent,
          scopeLevel: 'GLOBAL',
          scopeId: null,
          order: orderValue
        });
        savedActivityId = createResult?.data?.activity_insert?.id || null;
      }

    const typesWithImages = ['WORD_MEANING', 'DUAL_MEANING', 'WORD_SPLIT', 'TENSES', 'LONG_QUESTION_ANSWERS', 'MAKE_SENTENCES', 'JOIN_WORDS'];
    if (typesWithImages.includes(formData.activityType) && savedActivityId && formData.autoGenerateImages) {
      try {
        const parsed = JSON.parse(formData.baseContent);
        let hasMissingImage = false;
        
        if (formData.activityType === 'TENSES') {
          hasMissingImage = Array.isArray(parsed?.groups) &&
            parsed.groups.some(g => !g?.image && (g?.sentences?.[0]?.englishText || g?.sentences?.[0]?.languageText));
        } else if (formData.activityType === 'LONG_QUESTION_ANSWERS') {
          hasMissingImage = Array.isArray(parsed?.questions) &&
            parsed.questions.some(q => !q?.answer?.image && (q?.question?.englishText || q?.question?.languageText));
        } else if (formData.activityType === 'MAKE_SENTENCES') {
          hasMissingImage = Array.isArray(parsed?.sentences) &&
            parsed.sentences.some(s => !s?.image && (s?.englishWord || s?.languageWord));
        } else if (formData.activityType === 'JOIN_WORDS') {
          hasMissingImage = Array.isArray(parsed?.words) &&
            parsed.words.some(w => !w?.image && (w?.answerMeaning || w?.answer));
        } else {
          hasMissingImage = Array.isArray(parsed?.words) &&
            parsed.words.some(w => !w?.image && (w?.englishMeaning || w?.languageWord || w?.questionMeaning || w?.question));
        }

          if (hasMissingImage) {
            const fn = httpsCallable(getFunctions(), 'generateWordMeaningImages', { timeout: 540000 });
            await fn({
              activityId: savedActivityId,
              baseContent: formData.baseContent,
              chapterId: selectedChapter.id,
              language: (selectedChapter?.subject || 'language').toLowerCase(),
              grade: selectedChapter?.grade,
              activityType: formData.activityType
            });
          }
        } catch (genErr) {
          console.error('Auto image generation failed:', genErr);
          // Non-fatal: activity is already saved.
        }
      }

      resetForm();
      await loadActivities();
    } catch (err) {

      alert('Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit
  const handleEdit = (activity) => {
    setFormData({
      activityType: activity.activityType,
      baseContent: activity.baseContent,
      order: activity.order ?? '',
      autoGenerateImages: false
    });
    setEditingId(activity.id);
    setShowAddForm(true);
  };

  // Handle content popup
  const handleOpenContentPopup = (type) => {
    if (!formData.activityType) {
      alert('Please select an Activity Type first');
      return;
    }
    setContentType(type);
    setShowContentPopup(true);
  };

  const handleContentSave = (content) => {
    const jsonContent = JSON.stringify(content);
    if (contentType === 'base') {
      setFormData(prev => ({ ...prev, baseContent: jsonContent }));
    }
    setShowContentPopup(false);
    setContentType('');
  };

  // Handle delete
  const handleDelete = async (activity) => {
    if (!window.confirm(`Are you sure you want to delete this ${activity.activityType} activity? This will also delete all associated images and audio.`)) {
      return;
    }

    try {
      setSaving(true);
      
      // 1. Cleanup media for this specific activity
      try {
        const cleanupFn = httpsCallable(getFunctions(), 'cleanupMedia');
        await cleanupFn({
          chapterId: selectedChapter.id,
          subject: selectedChapter.subject,
          grade: selectedChapter.grade,
          activityType: activity.activityType
        });
      } catch (mediaErr) {
        console.error('Media cleanup failed:', mediaErr);
        // Continue with DB deletion even if media cleanup fails
      }

      // 2. Delete database record
      await deleteActivity({ id: activity.id });
      await loadActivities();
    } catch (err) {
      alert('Failed to delete activity');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 font-medium">Loading activities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Edit Activities</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="font-medium text-gray-700">Chapter:</span>
                <span className="ml-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-medium">
                  {selectedChapter?.title}
                </span>
              </div>
              <div className="flex items-center">
                <span className="font-medium text-gray-700">Subject:</span>
                <span className="ml-1 px-2 py-1 bg-green-100 text-green-800 rounded-md font-medium">
                  {selectedChapter?.subject}
                </span>
              </div>
              <div className="flex items-center">
                <span className="font-medium text-gray-700">Grade:</span>
                <span className="ml-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-md font-medium">
                  {selectedChapter?.grade}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center px-4 py-2 text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-sm"
              onClick={loadActivities}
              disabled={loading}
            >
              🔄 Refresh
            </button>
            <button
              className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium shadow-sm"
              onClick={onBack}
            >
              <FaTimes className="w-4 h-4 mr-2" />
              Back to Chapters
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="w-5 h-5 text-red-500 mr-3">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Add Activity Button */}
      <div className="mb-6">
        <button
          className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors shadow-sm ${
            saving 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          onClick={() => setShowAddForm(true)}
          disabled={saving}
        >
          <FaPlus className="w-4 h-4 mr-2" />
          Add New Activity
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingId ? 'Edit Activity' : 'Add New Activity'}
              </h2>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Activity Type *
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.activityType}
                      onChange={(e) => setFormData(prev => ({ ...prev, activityType: e.target.value }))}
                      required
                    >
                      <option value="">Select Activity Type</option>
                      {activityTypes.map(type => {
                        const isAlreadyUsed = validateActivityType(type.value, editingId);
                        return (
                          <option 
                            key={type.value} 
                            value={type.value}
                            disabled={isAlreadyUsed}
                            style={isAlreadyUsed ? { color: '#9CA3AF', backgroundColor: '#F3F4F6' } : {}}
                          >
                            {type.label}{isAlreadyUsed ? ' (Already exists)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 1"
                      value={formData.order}
                      onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Images
                    </label>
                    <div className="flex items-center h-[42px]">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          checked={formData.autoGenerateImages}
                          onChange={e => setFormData(prev => ({ ...prev, autoGenerateImages: e.target.checked }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">Auto-Generate</span>
                      </label>
                    </div>
                  </div>

                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base Content *
                    </label>
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                          !formData.activityType
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                        }`}
                        onClick={() => handleOpenContentPopup('base')}
                        disabled={!formData.activityType}
                      >
                        <FaCog className="w-4 h-4 mr-2" />
                        {formData.baseContent ? 'Edit' : 'Add'}
                      </button>
                      {formData.baseContent && (
                        <div className="flex items-center text-green-600">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          <span className="text-sm font-medium">Configured</span>
                        </div>
                      )}
                    </div>
                    <textarea
                      className="w-full mt-3 min-h-[220px] px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-y"
                      value={formData.baseContent}
                      onChange={(e) => setFormData(prev => ({ ...prev, baseContent: e.target.value }))}
                      placeholder={!formData.activityType ? 'Select an Activity Type first, then paste JSON here' : 'Paste base content JSON directly here'}
                      disabled={!formData.activityType}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      You can use the editor button above or paste valid JSON directly here.
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="w-5 h-5 text-blue-600 mr-2">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Scope Level:</span> Global (All activities are global by default)
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className={`flex items-center px-6 py-2 rounded-lg font-medium transition-colors ${
                      saving 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    disabled={saving}
                  >
                    <FaSave className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : (editingId ? 'Update Activity' : 'Create Activity')}
                  </button>
                  <button
                    type="button"
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    onClick={resetForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Activities List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaCog className="w-5 h-5 text-white mr-3" />
              <h2 className="text-xl font-semibold text-white">Activities Overview</h2>
            </div>
            <span className="px-3 py-1 bg-white bg-opacity-20 text-white rounded-full text-sm font-medium">
              {activities.length} Total
            </span>
          </div>
        </div>
        
        <div className="p-0">
          {activities.length === 0 ? (
            <div className="text-center py-16">
              <div className="mb-6">
                <FaCog className="w-16 h-16 text-gray-300 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Activities Created</h3>
              <p className="text-gray-600 mb-1">This chapter doesn&apos;t have any activities yet.</p>
              <p className="text-sm text-gray-500">Click &quot;Add New Activity&quot; above to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-2/5">
                      <div className="flex items-center">
                        <FaCog className="w-4 h-4 mr-2 text-gray-500" />
                        Activity Type
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 w-1/4">
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 mr-2 text-gray-500">
                          <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        Base Content
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 w-1/6">
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 mr-2 text-gray-500">
                          <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                          </svg>
                        </div>
                        Actions
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activities.map((activity, index) => (
                    <tr key={activity.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                      <td className="px-6 py-6">
                        <div>
                          <div className="text-lg font-semibold text-gray-900 mb-1">
                            {activityTypes.find(t => t.value === activity.activityType)?.label || activity.activityType}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6 text-center">
                        {activity.baseContent ? (
                          <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                            Configured
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800">
                            <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                            Not Set
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-6">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              saving 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                            }`}
                            onClick={() => handleEdit(activity)}
                            disabled={saving}
                            title="Edit Activity"
                          >
                            <FaEdit className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                          <button
                            className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              saving 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                            }`}
                            onClick={() => handleDelete(activity)}
                            disabled={saving}
                            title="Delete Activity"
                          >
                            <FaTrash className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Content Popup */}
      <ContentPopup
        show={showContentPopup}
        activityType={formData.activityType}
        contentType={contentType}
        onSave={handleContentSave}
        onClose={() => setShowContentPopup(false)}
        initialContent={formData.baseContent}
        selectedChapter={selectedChapter}
        activityId={editingId}
      />
    </div>
  );
};

export default AdminActivityEditor;
